import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './SlotMachineRotator.css'; // Ensure CSS is imported

// Helper function for modulo arithmetic (not needed here but good practice)
// const mod = (n, m) => ((n % m) + m) % m;

const SlotMachineRotator = ({
  words = [],
  lineHeight = 30,
  activeRange = [0.2, 0.8],
  transitionDuration = 0.3,
  scrollSensitivity = 3,
  stickToEnd = false,
  alignLeft = false,
  ...props
}) => {
  const wordsCount = words.length;
  const initialIndex = wordsCount;
  const [rawContinuousIndex, setRawContinuousIndex] = useState(initialIndex);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastViewportProgressRef = useRef(null);

  // Effect to reset state
  useEffect(() => {
    setRawContinuousIndex(initialIndex);
    lastViewportProgressRef.current = null;
  }, [words, wordsCount, initialIndex]);


  // Memoized function to calculate the raw index
  const calculateAndSetIndex = useCallback(() => {
    if (!containerRef.current || wordsCount <= 1) return;

    const element = containerRef.current;
    const elementRect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const activeTop = viewportHeight * activeRange[0];
    const activeBottom = viewportHeight * activeRange[1];
    const activeHeight = activeBottom - activeTop;
    const elementTop = elementRect.top;

    let currentViewportProgress = (activeBottom - elementTop) / activeHeight;
    currentViewportProgress = Math.max(0, Math.min(1, currentViewportProgress));

    if (stickToEnd) {
      const firstWordIndex = initialIndex;
      const lastWordIndex = initialIndex + wordsCount - 1;
      const targetElementTop = viewportHeight * 0.3;
      let endProgressThreshold = (activeBottom - targetElementTop) / activeHeight;
      endProgressThreshold = Math.max(0, Math.min(1, endProgressThreshold));

      let targetIndex;
      if (endProgressThreshold <= 0) {
          targetIndex = (currentViewportProgress > 0) ? lastWordIndex : firstWordIndex;
      } else if (currentViewportProgress >= endProgressThreshold) {
        targetIndex = lastWordIndex;
      } else {
        const effectiveProgress = currentViewportProgress / endProgressThreshold;
        targetIndex = firstWordIndex + (lastWordIndex - firstWordIndex) * effectiveProgress;
      }
      // Use floor/ceil or round when setting final index? maybe not needed if translateY centers it.
      setRawContinuousIndex(targetIndex);

    } else {
      // Infinite Scroll Logic
      if (lastViewportProgressRef.current === null) {
        lastViewportProgressRef.current = currentViewportProgress;
        return;
      }
      const deltaProgress = currentViewportProgress - lastViewportProgressRef.current;
      const indexChange = deltaProgress * scrollSensitivity;
      if (Math.abs(indexChange) > 0.001) {
          setRawContinuousIndex(prevIndex => prevIndex + indexChange);
      }
      lastViewportProgressRef.current = currentViewportProgress;
    }

    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); }

  }, [wordsCount, activeRange, scrollSensitivity, stickToEnd, initialIndex]);


  // Effect to handle scroll event listening
  useEffect(() => {
    if(wordsCount <= 1) return;
    const handleScroll = () => { animationFrameRef.current = requestAnimationFrame(calculateAndSetIndex); };
    const initTimeout = setTimeout(() => { requestAnimationFrame(calculateAndSetIndex); }, 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); }
       clearTimeout(initTimeout);
        lastViewportProgressRef.current = null;
    };
  }, [calculateAndSetIndex, wordsCount]);


  // --- MODIFIED: Adjust translateY for centering in 5-line container ---
  const translateY = (2 - rawContinuousIndex) * lineHeight;
  // --- END MODIFICATION ---

  // Prepare CSS variables
  const componentStyle = { ...props.style, '--line-height': `${lineHeight}px`, '--transition-duration': `${transitionDuration}s` };

  // Create the extended list for rendering
  const displayItems = wordsCount > 0 ? [
      ...words.map((word, index) => ({ word, originalIndex: index, key: `rep-0-${index}` })),
      ...words.map((word, index) => ({ word, originalIndex: index, key: `rep-1-${index}` })),
      ...words.map((word, index) => ({ word, originalIndex: index, key: `rep-2-${index}` })),
    ] : [];

  return (
    <div ref={containerRef} className={`slot-machine-container ${props.className || ''} ${alignLeft ? 'align-left' : ''}`} style={componentStyle} >
      <div className="slot-machine-reel" style={{ transform: `translateY(${translateY}px)` }} >
        {displayItems.map((item, i) => {
            const { word, key } = item;
            // Opacity calculation based on distance from rawContinuousIndex
            const distance = Math.abs(i - rawContinuousIndex);
            let opacity = 0;
            if (distance < 0.5) { opacity = 1.0; }
            else if (distance < 1.5) { opacity = 0.2; }
            else if (distance < 2.5) { opacity = 0.05; }
            return ( <span key={key} className="slot-machine-word" style={{ opacity: opacity }} > {word} </span> );
        })}
      </div>
    </div>
  );
};

// --- PropTypes and defaultProps remain the same ---
SlotMachineRotator.propTypes = {
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
  lineHeight: PropTypes.number,
  activeRange: PropTypes.arrayOf(PropTypes.number),
  transitionDuration: PropTypes.number,
  scrollSensitivity: PropTypes.number,
  stickToEnd: PropTypes.bool,
  alignLeft: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

SlotMachineRotator.defaultProps = {
    lineHeight: 30,
    activeRange: [0.2, 0.8],
    transitionDuration: 0.3,
    scrollSensitivity: 3,
    stickToEnd: false,
    alignLeft: false,
    className: '',
    style: {},
}

export default SlotMachineRotator;